import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useLocation, Link } from "react-router-dom";
import api, { formatApiError, API } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Avatar from "@/components/Avatar";
import VoiceRecorder from "@/components/VoiceRecorder";
import { toast } from "sonner";
import { ArrowLeft, Send, BookOpen, Download, X, Share2 } from "lucide-react";

const DAILY_OPENER =
  "Hey, just got a minute. What kind of day has it been so far? Anything pulling at you?";

const REGULAR_OPENER =
  "I'm here. What's on your mind — something to talk through, or do you just need to think out loud?";

function renderText(text) {
  // Lightweight markdown-ish rendering: bold + paragraphs + lists + inline images
  const lines = text.split("\n");
  const out = [];
  let listBuffer = [];
  const flushList = (key) => {
    if (listBuffer.length === 0) return;
    out.push(
      <ul key={`ul-${key}`}>
        {listBuffer.map((line, i) => (
          <li key={i} dangerouslySetInnerHTML={{ __html: inline(line.replace(/^[-*]\s+/, "")) }} />
        ))}
      </ul>
    );
    listBuffer = [];
  };
  // Match: ![alt](url) on its own line (or as its own paragraph block)
  const IMG_RE = /^!\[([^\]]*)\]\((https?:\/\/[^)\s]+)\)\s*$/;
  lines.forEach((raw, idx) => {
    const line = raw.trimEnd();
    const imgMatch = line.match(IMG_RE);
    if (imgMatch) {
      flushList(idx);
      const [, alt, url] = imgMatch;
      out.push(
        <img
          key={`img-${idx}`}
          src={url}
          alt={alt || "vibes"}
          className="rounded-xl my-2 max-w-full sm:max-w-[320px] block"
          loading="lazy"
        />
      );
      return;
    }
    if (/^[-*]\s+/.test(line)) {
      listBuffer.push(line);
    } else if (line.trim() === "") {
      flushList(idx);
    } else {
      flushList(idx);
      out.push(
        <p key={`p-${idx}`} dangerouslySetInnerHTML={{ __html: inline(line) }} />
      );
    }
  });
  flushList("end");
  return out;
}

function inline(text) {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/`([^`]+)`/g, "<code>$1</code>");
}

export default function Chat() {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [conv, setConv] = useState(null);
  const [persona, setPersona] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef(null);
  const greetedRef = useRef(false);
  const [showRecall, setShowRecall] = useState(false);
  const [recall, setRecall] = useState("");
  const [recallLoading, setRecallLoading] = useState(false);
  const [sharingId, setSharingId] = useState(null);

  // Avi (and any future "ai_tools" domain personas) get the one-tap share button.
  const isShareablePersona = (persona?.domain || "") === "ai_tools";

  const shareAsSnippet = async (msg) => {
    if (!msg?.message_id) return;
    setSharingId(msg.message_id);
    try {
      const { data } = await api.post(
        `/conversations/${conversationId}/messages/${msg.message_id}/snippet`,
      );
      const publicUrl = `${window.location.origin}/s/${data.slug}`;
      try {
        await navigator.clipboard.writeText(publicUrl);
        toast.success("Link copied — share it anywhere", {
          description: publicUrl,
        });
      } catch {
        toast.success("Snippet created", { description: publicUrl });
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSharingId(null);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/conversations/${conversationId}`);
        setConv(data.conversation);
        setPersona(data.persona);
        setMessages(data.messages || []);
      } catch (e) {
        toast.error(formatApiError(e));
        navigate("/dashboard", { replace: true });
      } finally {
        setLoading(false);
      }
    })();
  }, [conversationId, navigate]);

  // Friendly local greeting in the bubble area if no messages yet.
  useEffect(() => {
    if (loading || greetedRef.current) return;
    greetedRef.current = true;
    if (messages.length === 0 && persona) {
      const isDaily =
        location.state?.greet === "daily" || conv?.kind === "daily";
      const opener = isDaily ? DAILY_OPENER : REGULAR_OPENER;
      setMessages([
        {
          message_id: "greet",
          role: "assistant",
          content: opener,
          created_at: new Date().toISOString(),
          _local: true,
        },
      ]);
    }
  }, [loading, messages.length, persona, conv, location.state]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  const send = async (e) => {
    e?.preventDefault?.();
    const text = input.trim();
    if (!text || sending) return;
    setSending(true);
    setInput("");
    const optimistic = {
      message_id: `tmp_${Date.now()}`,
      role: "user",
      content: text,
      created_at: new Date().toISOString(),
      _local: true,
    };
    setMessages((prev) => [...prev, optimistic]);

    // Streaming assistant bubble (incrementally filled)
    const streamingId = `stream_${Date.now()}`;
    setMessages((prev) => [
      ...prev,
      {
        message_id: streamingId,
        role: "assistant",
        content: "",
        _streaming: true,
        _local: true,
        created_at: new Date().toISOString(),
      },
    ]);

    let assembled = "";
    const slowTimer = setTimeout(() => {
      // If still no content after 8s, flag the streaming bubble as 'slow'
      // so the UI can show a reassuring "Let me think about that for a second…" hint.
      if (!assembled) {
        setMessages((prev) =>
          prev.map((m) =>
            m.message_id === streamingId ? { ...m, _slow: true } : m
          )
        );
      }
    }, 8000);
    try {
      const res = await fetch(`${API}/conversations/${conversationId}/messages/stream`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ content: text }),
      });
      if (!res.ok || !res.body) {
        // Try to surface the server's friendly message (rate-limit,
        // paywall, monthly cap, etc.). Falls back to a generic error.
        let msg = `HTTP ${res.status}`;
        try {
          const j = await res.json();
          const d = j?.detail;
          if (typeof d === "string") msg = d;
          else if (d && typeof d === "object" && d.message) msg = d.message;
        } catch {
          /* not JSON — keep the fallback */
        }
        throw new Error(msg);
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      let realUserMsg = null;
      let realAssistantMsg = null;
      // SSE parsing: events are separated by \n\n; each event has 1+ lines beginning with "data: "
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        let idx;
        while ((idx = buf.indexOf("\n\n")) !== -1) {
          const raw = buf.slice(0, idx);
          buf = buf.slice(idx + 2);
          const lines = raw.split("\n");
          const dataLines = lines.filter((l) => l.startsWith("data: ")).map((l) => l.slice(6));
          if (dataLines.length === 0) continue;
          let evt;
          try {
            evt = JSON.parse(dataLines.join("\n"));
          } catch {
            continue;
          }
          if (evt.type === "user") {
            realUserMsg = evt.message;
          } else if (evt.type === "delta") {
            assembled += evt.text || "";
            setMessages((prev) =>
              prev.map((m) =>
                m.message_id === streamingId ? { ...m, content: assembled } : m
              )
            );
          } else if (evt.type === "replace") {
            // Server swapped tokens (e.g. [VIBES] → GIF). Replace bubble content wholesale.
            assembled = evt.text || assembled;
            setMessages((prev) =>
              prev.map((m) =>
                m.message_id === streamingId ? { ...m, content: assembled } : m
              )
            );
          } else if (evt.type === "done") {
            realAssistantMsg = evt.assistant_message;
          } else if (evt.type === "error") {
            throw new Error(evt.detail || "Stream error");
          }
        }
      }
      // Replace optimistic + streaming bubbles with real persisted messages
      setMessages((prev) => {
        const cleaned = prev.filter(
          (m) =>
            m.message_id !== optimistic.message_id &&
            m.message_id !== streamingId &&
            !(m._local && m.role === "assistant" && m.message_id === "greet")
        );
        const seen = new Set(cleaned.map((m) => m.message_id));
        const merged = [...cleaned];
        for (const real of [realUserMsg, realAssistantMsg].filter(Boolean)) {
          if (!seen.has(real.message_id)) {
            merged.push(real);
            seen.add(real.message_id);
          }
        }
        return merged;
      });
    } catch (e) {
      toast.error(e?.message || "Connection lost");
      setMessages((prev) =>
        prev.filter((m) => m.message_id !== optimistic.message_id && m.message_id !== streamingId)
      );
      setInput(text);
    } finally {
      clearTimeout(slowTimer);
      setSending(false);
    }
  };

  const openRecall = async () => {
    setShowRecall(true);
    setRecallLoading(true);
    setRecall("");
    try {
      const { data } = await api.post(`/conversations/${conversationId}/summary`);
      setRecall(data.recall);
    } catch (e) {
      toast.error(formatApiError(e));
      setShowRecall(false);
    } finally {
      setRecallLoading(false);
    }
  };

  const exportConversation = () => {
    const url = `${API}/conversations/${conversationId}/export`;
    // open in new tab; backend sends Content-Disposition: attachment
    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank";
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const onTranscript = (text) => {
    setInput((prev) => (prev ? `${prev} ${text}` : text));
  };

  const isStreaming = messages.some((m) => m._streaming);
  const streamingMsg = messages.find((m) => m._streaming);
  const isThinking = isStreaming && !streamingMsg?.content;
  const isReplying = isStreaming && !!streamingMsg?.content;

  return (
    <div data-testid="chat-page" className="min-h-screen bg-[#FBF9F6] flex flex-col">
      <Navbar />
      <main className="flex-1 max-w-6xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="lg:grid lg:grid-cols-[320px_1fr] lg:gap-10">

          {/* Desktop sticky sidebar — persona identity stays put */}
          {persona && (
            <aside
              data-testid="chat-persona-sidebar"
              className="hidden lg:block"
            >
              <div className="sticky top-24">
                <button
                  type="button"
                  onClick={() => navigate("/dashboard")}
                  data-testid="chat-back-btn-desktop"
                  className="mb-6 inline-flex items-center gap-1.5 text-sm text-[#6B655D] hover:text-[#2D2926] rounded-full px-3 py-1.5 hover:bg-[#F2EFE9] transition-colors"
                >
                  <ArrowLeft size={15} strokeWidth={1.5} />
                  Back
                </button>
                <div className="relative w-56 h-56 mx-auto">
                  {isStreaming && (
                    <>
                      {/* Outer breathing halo — big soft amber glow that
                         announces engagement. */}
                      <div
                        data-testid="persona-engagement-glow"
                        className="absolute -inset-8 rounded-full bg-[#D4A574]/60 blur-2xl animate-breathe"
                        aria-hidden="true"
                      />
                      {/* Inner shimmer — faster, tighter, warmer. Layers
                         under the breathe for a sense of active thought. */}
                      <div
                        className="absolute -inset-3 rounded-full bg-[#E8B87A]/50 blur-xl animate-shimmer"
                        aria-hidden="true"
                      />
                      {/* Slow rotating conic sweep — only while thinking,
                         so the portrait feels "listening" before it speaks. */}
                      {isThinking && (
                        <div
                          className="absolute -inset-4 rounded-full animate-sweep"
                          style={{
                            background:
                              "conic-gradient(from 0deg, transparent 0deg, rgba(212,165,116,0.55) 90deg, transparent 180deg, rgba(232,184,122,0.35) 270deg, transparent 360deg)",
                            filter: "blur(10px)",
                          }}
                          aria-hidden="true"
                        />
                      )}
                    </>
                  )}
                  <img
                    src={persona.avatar_url || "/jim-400.jpg"}
                    alt={persona.name}
                    className={`relative w-56 h-56 rounded-full object-cover ring-4 shadow-xl transition-[--tw-ring-color] duration-500 ${
                      isReplying
                        ? "ring-[#D4A574]"
                        : isThinking
                        ? "ring-[#E8B87A]"
                        : "ring-white"
                    }`}
                  />
                </div>
                <div className="mt-6 text-center">
                  <h1
                    data-testid="chat-persona-name-desktop"
                    className="font-serif text-3xl text-[#2D2926] leading-tight"
                  >
                    {persona.name}
                  </h1>
                  <p className="mt-1 text-xs uppercase tracking-[0.2em] text-[#9A938A]">
                    {persona.role || "Colleague"}
                  </p>
                  {persona.tagline && (
                    <p className="mt-4 text-sm italic text-[#6B655D] leading-relaxed">
                      &ldquo;{persona.tagline}&rdquo;
                    </p>
                  )}
                </div>
              </div>
            </aside>
          )}

          {/* Chat column */}
          <div className="flex flex-col">

        {/* Mobile / tablet compact header */}
        <div className="flex items-center gap-3 mb-6 lg:hidden">
          <button
            type="button"
            onClick={() => navigate("/dashboard")}
            data-testid="chat-back-btn"
            className="text-[#6B655D] hover:text-[#2D2926] rounded-full p-2 hover:bg-[#F2EFE9]"
          >
            <ArrowLeft size={18} strokeWidth={1.5} />
          </button>
          {persona && (
            <>
              <div className="relative">
                {isStreaming && (
                  <>
                    <div
                      className="absolute -inset-1.5 rounded-full bg-[#D4A574]/60 blur-md animate-breathe"
                      aria-hidden="true"
                    />
                    <div
                      className="absolute inset-0 rounded-full ring-2 ring-[#D4A574]/70 animate-pulse-ring"
                      aria-hidden="true"
                    />
                  </>
                )}
                <div className="relative">
                  <Avatar src={persona.avatar_url} name={persona.name} size={44} />
                </div>
              </div>
              <div className="min-w-0 flex-1">
                <h1
                  data-testid="chat-persona-name"
                  className="font-serif text-2xl text-[#2D2926] leading-none"
                >
                  {persona.name}
                </h1>
                <p className="text-xs text-[#9A938A] uppercase tracking-wider">
                  {persona.role || "Colleague"}
                </p>
              </div>
              <div className="ml-auto flex items-center gap-2">
                <button
                  type="button"
                  data-testid="recall-btn"
                  onClick={openRecall}
                  className="inline-flex items-center gap-1.5 text-sm text-[#6B655D] hover:text-[#2D2926] hover:bg-[#F2EFE9] rounded-full px-3 py-2 transition-colors"
                  title="Ask the persona to recall what you've talked about"
                >
                  <BookOpen size={15} strokeWidth={1.5} />
                  <span className="hidden sm:inline">Recall</span>
                </button>
                <button
                  type="button"
                  data-testid="export-btn"
                  onClick={exportConversation}
                  className="inline-flex items-center gap-1.5 text-sm text-[#6B655D] hover:text-[#2D2926] hover:bg-[#F2EFE9] rounded-full px-3 py-2 transition-colors"
                  title="Download as Markdown"
                >
                  <Download size={15} strokeWidth={1.5} />
                  <span className="hidden sm:inline">Export</span>
                </button>
              </div>
            </>
          )}
        </div>

        {/* Actions bar (desktop shows Recall / Export inline; mobile keeps them in the compact header row) */}
        <div className="hidden lg:flex justify-end mb-4 gap-2">
          {persona && (
            <>
              <button
                type="button"
                data-testid="recall-btn-desktop"
                onClick={openRecall}
                className="inline-flex items-center gap-1.5 text-sm text-[#6B655D] hover:text-[#2D2926] hover:bg-[#F2EFE9] rounded-full px-3 py-2 transition-colors"
                title="Ask the persona to recall what you've talked about"
              >
                <BookOpen size={15} strokeWidth={1.5} />
                Recall
              </button>
              <button
                type="button"
                data-testid="export-btn-desktop"
                onClick={exportConversation}
                className="inline-flex items-center gap-1.5 text-sm text-[#6B655D] hover:text-[#2D2926] hover:bg-[#F2EFE9] rounded-full px-3 py-2 transition-colors"
                title="Download as Markdown"
              >
                <Download size={15} strokeWidth={1.5} />
                Export
              </button>
            </>
          )}
        </div>

        {/* Messages */}
        <div
          ref={scrollRef}
          data-testid="chat-scroll"
          className="surface-card flex-1 overflow-y-auto p-4 sm:p-6 mb-4 bg-[#FBF9F6]/40"
          style={{ minHeight: "50vh", maxHeight: "65vh" }}
        >
          {loading ? (
            <p className="text-[#6B655D]">Loading conversation…</p>
          ) : (
            <ul className="space-y-4">
              {messages.map((m, i) => (
                <li
                  key={m.message_id || i}
                  className={`flex flex-col gap-1 ${m.role === "user" ? "items-end" : "items-start"}`}
                  data-testid={`message-${m.role}-${i}`}
                >
                  <div
                    className={`bubble max-w-[85%] sm:max-w-[75%] px-4 py-3 leading-relaxed text-[#2D2926] ${
                      m.role === "user"
                        ? "bg-[#F2EFE9] rounded-2xl rounded-br-sm"
                        : "bg-white border border-[#E6E2D8] rounded-2xl rounded-bl-sm shadow-sm"
                    }`}
                  >
                    {m._streaming && !m.content ? (
                      <span className="inline-flex items-center">
                        <span className="typing-dot" />
                        <span className="typing-dot" />
                        <span className="typing-dot" />
                        {m._slow && (
                          <span
                            data-testid="slow-hint"
                            className="ml-3 text-sm italic text-[#9A938A]"
                          >
                            Let me think about that for a second…
                          </span>
                        )}
                      </span>
                    ) : (
                      renderText(m.content)
                    )}
                  </div>
                  {isShareablePersona &&
                    m.role === "assistant" &&
                    !m._streaming &&
                    m.message_id &&
                    m.content && (
                      <button
                        type="button"
                        data-testid={`share-snippet-${i}`}
                        onClick={() => shareAsSnippet(m)}
                        disabled={sharingId === m.message_id}
                        className="text-[10px] uppercase tracking-wider text-[#9A938A] hover:text-[#D4A574] transition-colors flex items-center gap-1 ml-1 disabled:opacity-50"
                      >
                        <Share2 size={11} strokeWidth={1.5} />
                        {sharingId === m.message_id
                          ? "Sharing…"
                          : "Share as Card"}
                      </button>
                    )}
                </li>
              ))}
              {sending && !messages.some((m) => m._streaming) && (
                <li className="flex justify-start" data-testid="typing-indicator">
                  <div className="bubble bg-white border border-[#E6E2D8] rounded-2xl rounded-bl-sm px-4 py-3 shadow-sm">
                    <span className="typing-dot" />
                    <span className="typing-dot" />
                    <span className="typing-dot" />
                  </div>
                </li>
              )}
            </ul>
          )}
        </div>

        {/* Composer */}
        <form
          onSubmit={send}
          data-testid="chat-form"
          className="surface-card p-3 flex items-end gap-2"
        >
          <VoiceRecorder onTranscript={onTranscript} disabled={sending} />
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              // Shift+Enter sends the message. Plain Enter still inserts
              // a new line — see previous decision.
              if (e.key === "Enter" && e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            data-testid="chat-input"
            rows={2}
            placeholder={`Message ${persona?.name?.split(" ")[0] || "Colleague"}…`}
            className="flex-1 resize-none bg-transparent border-0 focus:outline-none px-3 py-2 text-[#2D2926] placeholder-[#9A938A]"
          />
          <button
            type="submit"
            data-testid="chat-send-btn"
            disabled={sending || !input.trim()}
            className="bg-[#2D2926] text-white hover:bg-[#1A1715] disabled:opacity-50 rounded-full w-11 h-11 flex items-center justify-center transition-colors"
            aria-label="Send"
          >
            <Send size={18} strokeWidth={1.5} />
          </button>
        </form>

        <div
          data-testid="composer-hint"
          className="mt-2 px-1 text-[11px] text-[#9A938A] flex items-center gap-1.5"
        >
          <span>Enter for a new line ·</span>
          <kbd className="px-1.5 py-0.5 rounded border border-[#E5DFD3] bg-white/60 font-sans text-[10px] text-[#6B655D]">
            Shift
          </kbd>
          <span>+</span>
          <kbd className="px-1.5 py-0.5 rounded border border-[#E5DFD3] bg-white/60 font-sans text-[10px] text-[#6B655D]">
            Enter
          </kbd>
          <span>to send</span>
        </div>

        <div className="mt-3 flex flex-wrap gap-2 text-xs text-[#9A938A]">
          <Link
            to="/feedback"
            data-testid="open-feedback-link"
            className="hover:text-[#2D2926] transition-colors"
          >
            Need feedback on a doc instead? →
          </Link>
        </div>

          </div>{/* /Chat column */}
        </div>{/* /grid */}
      </main>

      {/* Recall side panel */}
      {showRecall && (
        <div
          data-testid="recall-overlay"
          className="fixed inset-0 z-50 flex justify-end bg-[#2D2926]/30 backdrop-blur-sm"
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowRecall(false);
          }}
        >
          <aside
            data-testid="recall-panel"
            className="w-full max-w-md bg-[#FBF9F6] h-full overflow-y-auto p-8 shadow-2xl anim-fade-up"
          >
            <div className="flex items-center justify-between mb-6">
              <p className="text-xs uppercase tracking-[0.25em] text-[#9A938A]">
                Recall
              </p>
              <button
                type="button"
                data-testid="recall-close-btn"
                onClick={() => setShowRecall(false)}
                className="text-[#6B655D] hover:text-[#2D2926] rounded-full p-2 hover:bg-[#F2EFE9]"
                aria-label="Close"
              >
                <X size={16} strokeWidth={1.5} />
              </button>
            </div>
            {persona && (
              <div className="flex items-center gap-3 mb-6">
                <Avatar src={persona.avatar_url} name={persona.name} size={48} />
                <div>
                  <p className="font-serif text-xl text-[#2D2926] leading-tight">
                    {persona.name}
                  </p>
                  <p className="text-xs text-[#9A938A] uppercase tracking-wider">
                    Remembering our conversation
                  </p>
                </div>
              </div>
            )}
            {recallLoading ? (
              <p className="text-[#6B655D]">
                <span className="typing-dot" />
                <span className="typing-dot" />
                <span className="typing-dot" />
                <span className="ml-3">Recalling…</span>
              </p>
            ) : (
              <div className="font-body text-[#2D2926] leading-relaxed whitespace-pre-line text-lg">
                {recall}
              </div>
            )}
          </aside>
        </div>
      )}
    </div>
  );
}
